import { useQuery } from "@tanstack/solid-query";
import {
  type MetricDetail as MetricDetailData,
  MetricDetailSchema,
} from "contracts/api/metric/responses";
import { healthState } from "contracts/report/health";
import { createSignal, For, Show } from "solid-js";
import { SCAN_SECTION_KEY } from "../../enums/scan";
import { KEYBOARD_SCOPE } from "../../enums/shell";
import { setScope } from "../../keyboard/manager";
import { isAdmin } from "../../store/admin";
import { api } from "../../store/api";
import { queryClient } from "../../store/query";
import { healthLabel, healthVar } from "../../ui/health";
import { Sparkline } from "../../ui/Sparkline";
import { BlockViewer } from "../admin/BlockEditor";
import { ScanBreadcrumbs, ScanRouteSurface } from "../scan/ScanRouteChrome";
import { ScanShell } from "../scan/ScanShell";
import { createWebsiteStore } from "../website/store";
import { AttachedIssues } from "./AttachedIssues";
import { MetricEditor } from "./MetricEditor";
import { ThresholdPanel } from "./ThresholdPanel";

export function MetricDetail(props: { websiteId: string; metricKey: string }) {
  setScope(KEYBOARD_SCOPE.LIST);
  const context = createWebsiteStore(props.websiteId);
  const queryKey = ["metric-detail", props.websiteId, props.metricKey];
  const detail = useQuery(() => ({
    queryKey,
    queryFn: async () =>
      MetricDetailSchema.parse(
        await api.metric(props.websiteId, props.metricKey),
      ),
  }));
  const replace = (fresh: MetricDetailData) =>
    queryClient.setQueryData(queryKey, fresh);

  return (
    <ScanShell context={context} active={SCAN_SECTION_KEY.SIGNALS}>
      <ScanRouteSurface class="signal-metric-detail-route">
        <Show
          when={detail.data}
          keyed
          fallback={<div class="loading">Loading metric…</div>}
        >
          {(value) => (
            <>
              <ScanBreadcrumbs
                items={[
                  {
                    label: "Signal Scan",
                    href: `/w/${props.websiteId}/scan`,
                  },
                  {
                    label: "Scorecard",
                    href: `/w/${props.websiteId}/scan/signals`,
                  },
                  { label: value.metric.label },
                ]}
              />
              <DetailView
                value={value}
                metricKey={props.metricKey}
                context={context}
                onSaved={replace}
              />
            </>
          )}
        </Show>
      </ScanRouteSurface>
    </ScanShell>
  );
}

function DetailView(props: {
  value: MetricDetailData;
  metricKey: string;
  context: ReturnType<typeof createWebsiteStore>;
  onSaved: (fresh: MetricDetailData) => void;
}) {
  const [editing, setEditing] = createSignal(false);
  const score = () => props.value.signal?.score ?? null;
  const state = () =>
    healthState(score(), props.value.metric.good, props.value.metric.watch);

  return (
    <>
      <div class="page-head detail-head">
        <div>
          <div class="detail-pillar">{props.value.metric.pillar}</div>
          <h1 class="page-title">{props.value.metric.label}</h1>
        </div>
        <div class="detail-head-actions">
          <Show when={isAdmin()}>
            <button class="btn" type="button" onClick={() => setEditing(true)}>
              Edit metric
            </button>
          </Show>
          <div class="detail-score-wrap">
            <div
              class="detail-score mono"
              style={{ color: healthVar(state()) }}
            >
              {score() === null ? "—" : score()}
            </div>
            <span class={`sig-state st-${state()}`}>
              {healthLabel(state())}
            </span>
          </div>
        </div>
      </div>
      <div class="detail-grid">
        <section class="card panel">
          <div class="panel-label">Signal trend</div>
          <div class="panel-chart">
            <Sparkline
              data={props.value.signal?.trend ?? []}
              width={460}
              height={100}
              color={healthVar(state())}
              target={props.value.metric.target}
              strokeWidth={2}
              id={`metric-${props.metricKey}`}
            />
          </div>
        </section>
        <ThresholdPanel
          metric={props.value.metric}
          score={score()}
          state={state()}
        />
      </div>
      <section class="card panel">
        <div class="panel-label">What this measures</div>
        <p class="detail-desc">{props.value.metric.description}</p>
      </section>
      <section class="card panel">
        <div class="panel-label">Latest signal detail</div>
        <p class="detail-desc">{props.value.signal?.detail ?? "No data yet"}</p>
      </section>
      <section class="card panel">
        <div class="panel-label">How this measure is calculated</div>
        <p class="detail-desc">
          The stored measure detail explains the inputs and scoring context for
          this result.
        </p>
        <details class="scan-disclosure scan-technical-detail">
          <summary>Technical detail</summary>
          <div class="scan-disclosure-body">
            <BlockViewer blocks={props.value.blocks} />
          </div>
        </details>
      </section>
      <section class="card panel">
        <div class="panel-label">Pages measured</div>
        <Show
          when={props.value.pages.length > 0}
          fallback={<p class="detail-desc">No page-level data yet</p>}
        >
          <div class="metric-page-list">
            <For each={props.value.pages}>
              {(page) => (
                <a
                  class="metric-page-row"
                  href={`/w/${props.context.websiteId}/scan/pages/${page.pageId}`}
                >
                  <span>
                    <strong>{page.title ?? page.path}</strong>
                    <small class="mono">{page.path}</small>
                    <small>{page.detail}</small>
                  </span>
                  <span class="score-pill mono">{page.score}</span>
                </a>
              )}
            </For>
          </div>
        </Show>
      </section>
      <AttachedIssues issues={props.value.issues} context={props.context} />
      <Show when={editing()}>
        <MetricEditor
          websiteId={props.context.websiteId}
          metricKey={props.metricKey}
          value={props.value}
          onClose={() => setEditing(false)}
          onSaved={(fresh) => {
            props.onSaved(fresh);
            setEditing(false);
          }}
        />
      </Show>
    </>
  );
}
